/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Box, Paper, Typography } from '@mui/material'
import { HazardScenario } from '../types/hazard.types'
import {
  formatParamLabel,
  formatParamValue,
  getSedimentLabel,
  isSedimentValue,
} from '../utils/hazardScenarioGrid.utils'
import { HazardSedimentPill } from './HazardSedimentPill'
import { isMeaningfulParamValue } from '../utils/hazard.constants'

const SEDIMENT_KEY = 'sediment'
const isSedimentKey = (key: string) => key === SEDIMENT_KEY

type HazardActiveScenarioBarProps = {
  scenario: HazardScenario
}

export const HazardActiveScenarioBar = ({ scenario }: HazardActiveScenarioBarProps) => {
  const { parameters } = scenario

  const meaningfulParams = Object.entries(parameters).filter(([, value]) =>
    isMeaningfulParamValue(value),
  )

  return (
    <Paper data-testid="hazard-active-scenario-bar" css={rootStyles}>
      <Typography
        data-testid="hazard-active-scenario-bar-title"
        variant="caption"
        color="text.secondary"
        fontWeight={600}
      >
        ACTIVE HAZARD SCENARIO
      </Typography>

      <Box data-testid="hazard-active-scenario-bar-details" css={detailsStyles}>
        {meaningfulParams.map(([key, value]) => {
          if (isSedimentKey(key) && isSedimentValue(value)) {
            const label = getSedimentLabel(String(value))
            return (
              <Box
                key={key}
                data-testid={`hazard-active-scenario-bar-${key}`}
                css={sedimentContentStyles}
              >
                <Typography variant="body2">
                  <strong>{formatParamLabel(key)}:</strong>
                </Typography>
                <HazardSedimentPill
                  label={label}
                  data-testid={`hazard-active-scenario-bar-${key}-pill`}
                />
                <Typography variant="body2" color="text.secondary">
                  {String(value)}
                </Typography>
              </Box>
            )
          }

          return (
            <Typography key={key} data-testid={`hazard-active-scenario-bar-${key}`} variant="body2">
              <strong
                css={css`
                  text-transform: uppercase;
                `}
              >
                {formatParamLabel(key)}:
              </strong>{' '}
              {formatParamValue(key, value)}
            </Typography>
          )
        })}
      </Box>
    </Paper>
  )
}

const rootStyles = css`
  padding: 16px;
  margin-bottom: 24px;
`

const detailsStyles = css`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`

const sedimentContentStyles = css`
  display: flex;
  align-items: center;
  gap: 4px;
`
